"use client";
import { createLogAdapter } from "@/lib/logger/console-adapter";

import type { DelegationIdentity } from "@icp-sdk/core/identity";
import { Check, Globe, Search, Settings, User, Wallet } from "lucide-react";
import Image from "next/image";
import { usePathname, useRouter } from "next/navigation";
import type React from "react";
import { memo, useEffect, useRef, useState } from "react";
import SignInModal from "@/components/auth/sign-in-modal";
import SettingsPanel from "@/components/layout/SettingsPanel";
import { AccountAssetsModal } from "@/components/shared/AccountAssetsModal";
import { GlobalScanEntry } from "@/components/shared/global-scan-entry";
import { UserAccountPanel } from "@/components/shared/UserAccountPanel";
import {
	Drawer,
	DrawerContent,
	DrawerDescription,
	DrawerHeader,
	DrawerTitle,
} from "@/components/ui/drawer";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useIdentity } from "@/contexts/IdentityContext";
import { useVip } from "@/contexts/VipContext";
import { useLanguage } from "@/hooks/useLanguage";
import { useNotifications } from "@/hooks/useNotifications";
import {
	type PlatformDisplayInfo,
	useBabyOdinPlatformInfo,
} from "@/hooks/useBabyOdinPlatformInfo";
import { VipLevel } from "@/types/vip";

const uiLog = createLogAdapter("components/shared/MobileHeader.tsx");

interface MobileHeaderProps {
	backAction?: React.ReactNode;
	leftAction?: React.ReactNode;
	rightActions?: React.ReactNode[];
	className?: string;
}

interface MobileHeaderLogoMenuProps {
	currentSite: (typeof sites)[0];
	language: string;
	onSiteChange: (site: (typeof sites)[0]) => void;
}

// 站点配置
const sites = [
	{
		id: "odin",
		name: "OdinFun",
		url: "https://odin.baby",
		description: "odin.baby",
		icon: "🌐",
	},
	{
		id: "fomowell",
		name: "FomoWell",
		url: "https://fomowell.baby",
		description: "fomowell.baby",
		icon: "🌐",
	},
];

const MOBILE_HEADER_LOGO_SRC = "/apple-touch-icon.png";

const MobileHeaderLogoMenu = memo(function MobileHeaderLogoMenu({
	currentSite,
	language,
	onSiteChange,
}: MobileHeaderLogoMenuProps) {
	const router = useRouter();
	const [, setIsOpen] = useState(false);
	const [isLogoLoaded, setIsLogoLoaded] = useState(false);
	const { platforms } = useBabyOdinPlatformInfo();

	const formatPlatformPriceUsd = (priceUsd: number | null | undefined) => {
		if (priceUsd === null || priceUsd === undefined) return "$--";
		return `$${priceUsd.toFixed(4)}`;
	};

	const handlePlatformOpen = (platform: PlatformDisplayInfo) => {
		window.open(platform.buyLink, "_blank", "noopener,noreferrer");
	};

	return (
		<DropdownMenu onOpenChange={setIsOpen}>
			<DropdownMenuTrigger asChild>
				<button
					type="button"
					className="relative flex h-10 w-10 items-center justify-center overflow-hidden rounded-full transition-colors active:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-300 dark:active:bg-gray-700 dark:focus-visible:ring-gray-600"
					aria-label={
						language === "zh" ? "站点与价格信息" : "Site and price menu"
					}
					style={{
						WebkitTapHighlightColor: "transparent",
						touchAction: "manipulation",
					}}
				>
					<span
						aria-hidden="true"
						className={`absolute inset-0 flex items-center justify-center transition-opacity ${
							isLogoLoaded ? "opacity-0" : "opacity-100"
						}`}
					>
						<Globe className="h-4 w-4 text-gray-500 dark:text-gray-300" />
					</span>
					<Image
						src={MOBILE_HEADER_LOGO_SRC}
						alt="BabyOdin"
						width={28}
						height={28}
						priority
						className={`rounded-full transition-opacity ${
							isLogoLoaded ? "opacity-100" : "opacity-0"
						}`}
						onLoad={() => setIsLogoLoaded(true)}
					/>
				</button>
			</DropdownMenuTrigger>
			<DropdownMenuContent className="z-layer-floating w-72 rounded-2xl">
				<DropdownMenuLabel>
					{language === "zh" ? "选择站点" : "Select Site"}
				</DropdownMenuLabel>
				<DropdownMenuSeparator />

				{sites.map((site) => (
					<DropdownMenuItem
						key={site.id}
						onClick={() => onSiteChange(site)}
						className={site.id === currentSite.id ? "bg-accent" : ""}
					>
						<div className="flex w-full items-center justify-between">
							<div className="flex items-center gap-3">
								<Globe className="h-4 w-4" />
								<div>
									<div className="font-medium">{site.name}</div>
									<div className="text-xs text-muted-foreground">
										{site.description}
									</div>
								</div>
							</div>
							{currentSite.id === site.id ? (
								<Check className="h-4 w-4" />
							) : null}
						</div>
					</DropdownMenuItem>
				))}

				<DropdownMenuSeparator />
				<DropdownMenuLabel className="flex items-center justify-between gap-3">
					<div className="flex min-w-0 items-center gap-2">
						<Image
							src={MOBILE_HEADER_LOGO_SRC}
							alt="BabyOdin"
							width={16}
							height={16}
							priority
						/>
						<span>BabyOdin {language === "zh" ? "价格" : "Price"}</span>
					</div>
					<button
						type="button"
						onClick={() => router.push("/plaza/token/2rfi")}
						className="rounded-full border border-emerald-500/30 bg-emerald-500 px-2.5 py-1 text-xs font-medium leading-none text-white transition-colors hover:bg-emerald-600"
					>
						{language === "zh" ? "购买BabyOdin" : "Buy BabyOdin"}
					</button>
				</DropdownMenuLabel>
				<DropdownMenuSeparator />
				<div className="space-y-1 px-2 py-2">
					{platforms.map((platform) => {
						const isPending =
							platform.status === "loading" || platform.isRefreshing;

						return (
							<DropdownMenuItem
								key={platform.id}
								onSelect={() => handlePlatformOpen(platform)}
								title={`${platform.name}: ${platform.satsPriceLabel}`}
								className="cursor-pointer rounded-md px-2 py-2"
							>
								<div className="flex w-full items-center justify-between text-sm">
									<div className="flex items-center gap-2">
										<Image
											src={platform.logoSrc}
											alt=""
											width={16}
											height={16}
											className={`h-4 w-4 rounded-sm object-contain ${
												isPending ? "opacity-60" : "opacity-100"
											}`}
										/>
										<span className="rounded bg-gray-100 px-1.5 py-0.5 text-xs font-medium dark:bg-gray-800">
											{platform.name}
										</span>
									</div>
									<span
										className={`font-mono font-medium tabular-nums ${
											isPending ? "animate-pulse opacity-70" : ""
										}`}
									>
										{formatPlatformPriceUsd(platform.priceUsd)}
									</span>
								</div>
							</DropdownMenuItem>
						);
					})}
				</div>
			</DropdownMenuContent>
		</DropdownMenu>
	);
});

export default function MobileHeader({
	backAction,
	leftAction,
	rightActions,
	className,
}: MobileHeaderProps) {
	const { language } = useLanguage();
	const pathname = usePathname();
	const router = useRouter();

	const [isSettingsOpen, setIsSettingsOpen] = useState(false);
	const [currentSite, setCurrentSite] = useState(sites[0]); // 默认 OdinFun
	const [currentTokenData, setCurrentTokenData] = useState<any>(null);

	// 钱包登录相关状态
	const [isSignInModalOpen, setIsSignInModalOpen] = useState(false);
	const [isAccountDrawerOpen, setIsAccountDrawerOpen] = useState(false);
	const [isAssetsModalOpen, setIsAssetsModalOpen] = useState(false);
	const headerRef = useRef<HTMLElement | null>(null);
	const [delegationExpiration, setDelegationExpiration] = useState<
		bigint | null
	>(null);
	const { isAuthenticated, principal, btcAddress, setAuthData, clearAuth } =
		useIdentity();
	const { vipInfo, isVip } = useVip();
	const {
		notifications,
		unreadCount,
		isLoading: isNotificationsLoading,
		markAsRead,
		markAllAsRead,
	} = useNotifications(isAuthenticated ? principal : undefined);

	// 检查是否在代币详情页面
	const isTokenPage =
		pathname?.startsWith("/plaza/token/") && pathname.split("/").length === 4;
	const currentTokenId = isTokenPage ? pathname.split("/")[3] : null;

	// 获取当前代币数据（如果在代币页面）
	useEffect(() => {
		if (!currentTokenId) {
			setCurrentTokenData(null);
			return;
		}

		const fetchCurrentTokenData = async () => {
			try {
				const response = await fetch(
					`https://api.odin.fun/v1/token/${currentTokenId}`,
				);
				if (response.ok) {
					const data = await response.json();
					setCurrentTokenData(data);
				}
			} catch (error) {
				uiLog.error("Error fetching current token data:", error);
			}
		};

		fetchCurrentTokenData();
		const interval = setInterval(fetchCurrentTokenData, 60000); // Update every minute
		return () => clearInterval(interval);
	}, [currentTokenId]);

	// 站点切换处理
	const handleSiteChange = (site: (typeof sites)[0]) => {
		setCurrentSite(site);
		window.location.href = site.url;
	};

	// 钱包登录成功处理
	const handleLoginSuccess = (
		newPrincipal: string,
		address: string,
		newIdentity: DelegationIdentity,
	) => {
		setAuthData(newPrincipal, address, newIdentity);
		setIsSignInModalOpen(false);
	};

	// 退出登录处理
	const handleLogout = () => {
		try {
			clearAuth();
			setIsAccountDrawerOpen(false);
			// 确保状态更新
			window.location.reload();
		} catch (error) {
			uiLog.error("退出登录失败:", error);
		}
	};

	// 获取委托有效期
	useEffect(() => {
		if (isAuthenticated) {
			const storedDelegationResult = localStorage.getItem(
				"user_delegation_result",
			);
			if (storedDelegationResult) {
				try {
					const parsedChain = JSON.parse(
						storedDelegationResult,
						(key, value) => {
							if (key === "expiration" && typeof value === "string") {
								return BigInt(value);
							}
							return value;
						},
					);

					if (parsedChain?.delegationData?.delegation?.expiration) {
						setDelegationExpiration(
							BigInt(parsedChain.delegationData.delegation.expiration),
						);
					}
				} catch (error) {
					uiLog.error("Failed to parse delegation expiration:", error);
				}
			}
		}
	}, [isAuthenticated]);

	// 计算剩余有效期
	const getExpirationInfo = () => {
		if (!delegationExpiration) return null;

		const now = BigInt(Date.now()) * BigInt(1000000); // 转换为纳秒
		const remaining = delegationExpiration - now;

		if (remaining <= BigInt(0)) {
			return { expired: true, text: language === "zh" ? "已过期" : "Expired" };
		}

		const remainingMs = Number(remaining / BigInt(1000000)); // 转换为毫秒
		const hours = Math.floor(remainingMs / (1000 * 60 * 60));
		const minutes = Math.floor((remainingMs % (1000 * 60 * 60)) / (1000 * 60));

		let text;
		if (hours >= 24) {
			const days = Math.floor(hours / 24);
			const remainingHours = hours % 24;
			text =
				language === "zh"
					? `授权有效期剩余${days}天${remainingHours}小时`
					: `${days}d ${remainingHours}h left`;
		} else if (hours > 0) {
			text =
				language === "zh"
					? `授权有效期剩余${hours}小时${minutes}分钟`
					: `${hours}h ${minutes}m left`;
		} else {
			text =
				language === "zh"
					? `授权有效期剩余${minutes}分钟`
					: `${minutes} minutes left`;
		}

		// 格式化过期时间
		const expirationDate = new Date(
			Number(delegationExpiration / BigInt(1000000)),
		);
		const dateText =
			language === "zh"
				? `有效期至: ${expirationDate.toLocaleString("zh-CN")}`
				: `Valid until: ${expirationDate.toLocaleString()}`;

		return { expired: false, text, dateText, isLowTime: hours < 1 };
	};

	// 获取VIP等级对应的颜色
	const getVipColors = () => {
		switch (vipInfo.level) {
			case VipLevel.LEVEL_1:
				return {
					iconColor: "text-amber-500",
					dotColor: "bg-amber-500",
					textColor: "text-amber-600 dark:text-amber-400",
				};
			case VipLevel.LEVEL_2: // Gold Member - Gold
				return {
					iconColor: "text-yellow-500",
					dotColor: "bg-yellow-500",
					textColor: "text-yellow-600 dark:text-yellow-400",
				};
			default: // Normal User - Default colors
				return {
					iconColor: "text-gray-700 dark:text-gray-300",
					dotColor: "bg-green-500",
					textColor: "text-gray-700 dark:text-gray-300",
				};
		}
	};

	const vipColors = getVipColors();
	const openSearchPage = () => {
		router.push("/search");
	};
	const formatHeaderTokenPrice = (rawPrice: number) => {
		const satsPrice = Number(rawPrice || 0) / 1000;
		if (satsPrice > 100) {
			return `${Math.round(satsPrice).toLocaleString()} Sats`;
		}
		return `${satsPrice.toFixed(3)} Sats`;
	};

	useEffect(() => {
		const headerElement = headerRef.current;
		if (!headerElement || typeof document === "undefined") return;

		const rootStyle = document.documentElement.style;
		const updateHeaderHeight = () => {
			rootStyle.setProperty(
				"--mobile-header-height",
				`${headerElement.offsetHeight}px`,
			);
		};

		updateHeaderHeight();

		if (typeof ResizeObserver === "undefined") return;

		const resizeObserver = new ResizeObserver(updateHeaderHeight);
		resizeObserver.observe(headerElement);

		return () => {
			resizeObserver.disconnect();
		};
	}, []);

	return (
		<>
			<header
				ref={headerRef}
				className={`z-layer-mobile-header sticky top-0 flex h-14 shrink-0 items-center justify-between bg-white px-2 shadow-sm dark:bg-gray-800 ${className}`}
			>
				<div className="flex min-w-0 items-center gap-1">
					{backAction}
					<MobileHeaderLogoMenu
						currentSite={currentSite}
						language={language}
						onSiteChange={handleSiteChange}
					/>
					{leftAction}
				</div>

				{currentTokenData && isTokenPage ? (
					<div
						className="mx-2 flex flex-1 cursor-pointer items-center justify-center"
						onClick={() => window.location.reload()}
					>
						<div className="text-center">
							<div className="text-sm font-bold">
								{currentTokenData.ticker || currentTokenData.name}
							</div>
							<div className="text-xs text-gray-500 dark:text-gray-400">
								#{currentTokenId} ·{" "}
								{formatHeaderTokenPrice(Number(currentTokenData.price))}
							</div>
						</div>
					</div>
				) : (
					<button
						type="button"
						onClick={openSearchPage}
						className="mx-2 flex h-10 min-w-0 flex-1 items-center rounded-full border border-black/10 bg-[#f6f7f9] px-3 text-left shadow-sm transition-colors hover:bg-white dark:border-white/10 dark:bg-[#14171d] dark:hover:bg-[#171a21]"
						aria-label={language === "zh" ? "打开搜索页面" : "Open search"}
					>
						<Search className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />
						<span className="truncate text-sm text-muted-foreground">
							{language === "zh"
								? "搜索符文 / 身份ID / BTC地址 / 俚语 / 名称"
								: "Search runes, identity IDs, BTC addresses, aliases, or names"}
						</span>
					</button>
				)}

				<div className="flex items-center justify-end space-x-1">
					{rightActions}
					{isTokenPage ? (
						<button
							onClick={openSearchPage}
							className="rounded-full p-2 transition-colors hover:bg-gray-100 dark:hover:bg-gray-700"
							aria-label={language === "zh" ? "打开搜索页面" : "Open search"}
						>
							<Search className="h-5 w-5 text-gray-700 dark:text-gray-300" />
						</button>
					) : null}
					<GlobalScanEntry
						buttonClassName="rounded-full p-2 transition-colors hover:bg-gray-100 dark:hover:bg-gray-700"
						iconClassName="h-6 w-6 text-gray-700 dark:text-gray-300"
						ariaLabel={language === "zh" ? "打开扫一扫" : "Open scanner"}
					/>
					{isAuthenticated ? (
						<>
							<div className="relative">
								<button
									onClick={() => setIsAccountDrawerOpen(true)}
									className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors relative"
									aria-label={
										language === "zh" ? "钱包管理" : "Wallet Management"
									}
									aria-haspopup="dialog"
									aria-expanded={isAccountDrawerOpen}
								>
									<User className={`w-6 h-6 ${vipColors.iconColor}`} />
									{unreadCount > 0 ? (
										<div className="absolute -right-1 -top-1 flex min-w-5 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-semibold leading-5 text-white ring-2 ring-white dark:ring-gray-900">
											{unreadCount > 99 ? "99+" : unreadCount}
										</div>
									) : (
										<div
											className={`absolute -top-1 -right-1 w-3 h-3 ${vipColors.dotColor} rounded-full border-2 border-white dark:border-gray-800`}
										></div>
									)}
								</button>
							</div>
						</>
					) : (
						<button
							onClick={() => setIsSignInModalOpen(true)}
							className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
							aria-label={language === "zh" ? "连接钱包" : "Connect Wallet"}
						>
							<Wallet className={`w-6 h-6 ${vipColors.iconColor}`} />
						</button>
					)}

					<button
						onClick={() => setIsSettingsOpen(true)}
						className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
						aria-label={language === "zh" ? "打开设置" : "Open settings"}
					>
						<Settings className="w-6 h-6 text-gray-700 dark:text-gray-300" />
					</button>
				</div>
			</header>
			<SettingsPanel
				isOpen={isSettingsOpen}
				onClose={() => setIsSettingsOpen(false)}
			/>
			<Drawer open={isAccountDrawerOpen} onOpenChange={setIsAccountDrawerOpen}>
				<DrawerContent className="max-h-[88vh] overflow-hidden" showClose>
					<DrawerHeader className="sr-only">
						<DrawerTitle>
							{language === "zh" ? "账户中心" : "Account Center"}
						</DrawerTitle>
						<DrawerDescription>
							{language === "zh"
								? "查看账户信息、通知和快捷操作"
								: "View account details, notifications, and quick actions"}
						</DrawerDescription>
					</DrawerHeader>
					{isAuthenticated && principal ? (
						<UserAccountPanel
							language={language}
							principal={principal}
							btcAddress={btcAddress}
							isVip={isVip}
							vipInfo={vipInfo}
							vipColors={vipColors}
							notifications={notifications}
							unreadCount={unreadCount}
							isNotificationsLoading={isNotificationsLoading}
							onMarkAllAsRead={markAllAsRead}
							onMarkAsRead={markAsRead}
							onLogout={handleLogout}
							onClose={() => setIsAccountDrawerOpen(false)}
							onOpenAssets={() => setIsAssetsModalOpen(true)}
							expirationInfo={getExpirationInfo()}
							className="pb-safe"
							notificationLimit={5}
						/>
					) : null}
				</DrawerContent>
			</Drawer>
			<AccountAssetsModal
				open={isAssetsModalOpen}
				onOpenChange={setIsAssetsModalOpen}
				language={language}
				principal={principal}
			/>

			{/* 钱包登录弹窗 */}
			<SignInModal
				open={isSignInModalOpen}
				onOpenChange={setIsSignInModalOpen}
				onSuccess={handleLoginSuccess}
			/>
		</>
	);
}
